import { useRequest, APIError } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { useClientPortalAPI } from "@certn/api/src/useClientPortalAPI";
import {
  DangerBadge,
  Divider,
  GhostButton,
  MediaQuery,
  NewBadge,
  SecondaryTopNav,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  mq,
  showToast,
} from "@certn/cascadia-react";
import { useMediaQuery } from "@certn/cascadia-react/hooks/useMediaQuery";
import { breakpoints } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useResolvedPath,
} from "react-router-dom";

import { ActivityLogTab } from "./tabs/ActivityLogTab";
import { CommentSectionTab } from "./tabs/CommentSectionTab";
import { CredentialsTab } from "./tabs/CredentialsTab";
import { IdentityVerificationTab } from "./tabs/IdentityVerificationTab";
import { LinkedCasesTab } from "./tabs/LinkedCasesTab";
import { ReportTab } from "./tabs/ReportTab";
import { ConfigurationError } from "../../components/ConfigurationError";
import { CaseDetailsTitleCard } from "../../components/case-details-title-card/CaseDetailsTitleCard";
import { CommentSection } from "../../components/comment-section/CommentSection";
import { CopyLinkButton } from "../../components/copy-link-button/CopyLinkButton";
import { usePermissions } from "../../components/permissions/PermissionsProvider";
import { Previous } from "../../types";
import { EditRescreenModal } from "../person-profile/components/EditRescreenModal";
import { ScheduleRescreenModal } from "../person-profile/components/ScheduleRescreenModal";
import { useRefreshEligibleApplicants } from "../monitoring/useMonitoringApi";
import { AdjudicationTab } from "./tabs/AdjudicationTab";
import { DocumentsTab } from "./tabs/DocumentsTab";

export function CaseDetailsPage() {
  const { caseId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const url = useResolvedPath("").pathname;
  const currentTab = pathname.replace(url, "");
  const [previous] = useState<Previous>(state?.last || { href: "/cases" });
  const [showScheduleRescreenModal, setShowScheduleRescreenModal] =
    useState(false);
  const [showEditRescreenModal, setShowEditRescreenModal] = useState(false);
  const [promptMonitoringEnroll, setPromptMonitoringEnroll] = useState(false);
  const [monitoringEligibilityRefreshing, setMonitoringEligibilityRefreshing] =
    useState(false);

  useEffect(() => {
    setPromptMonitoringEnroll(false);
    setMonitoringEligibilityRefreshing(false);
  }, [caseId]);

  const request = useRequest();
  const refreshEligibleApplicants = useRefreshEligibleApplicants();
  const {
    clientPortalDisplayGuarantorsCoapplicants,
    clientPortalShowCredentialsOnCaseDetailsPage,
    enableClientAdjudication,
    enableDocumentsTabInClientPortal,
  } = useFlags();
  const isMobile = useMediaQuery(
    `(max-width: ${breakpoints["breakpoint-md"].maxWidth.toString()}px)`
  );
  const { hasPermission } = usePermissions();
  const {
    data: caseDetails,
    isLoading: caseDetailsIsLoading,
    mutate: updateCaseDetails,
  } = useClientPortalAPI(`/api/internal/client-portal/case/${caseId}/`);
  const { data: linkedCases = [] } = useClientPortalAPI(
    `/api/internal/client-portal/case/${caseId}/linked-cases/`
  );

  const { data: tabDetails, mutate: updateTabDetails } = useClientPortalAPI(
    `/api/internal/client-portal/case/${caseId}/tabs/`
  );

  useClientPortalAPI(
    `/api/internal/client-portal/case/${caseId}/identification-verified-details/`
  );

  const credentialRequest = caseDetails?.credential_request;

  const showAdjudicationTab =
    enableClientAdjudication &&
    caseDetails?.adjudication_rubric &&
    caseDetails?.overall_status?.name === "COMPLETE";

  async function updateDecision(
    caseShortId: string | undefined,
    decision: string
  ) {
    if (decision === "NONE") decision = "RESET";

    try {
      await request(
        `/api/internal/client-portal/case/${caseShortId}/set-client-decision/`,
        { method: "POST", body: JSON.stringify({ decision }) }
      );
      await updateCaseDetails();
      if (decision === "MEETS_CRITERIA") {
        setPromptMonitoringEnroll(true);
        setMonitoringEligibilityRefreshing(true);
        void refreshEligibleApplicants()
          .finally(() => setMonitoringEligibilityRefreshing(false))
          .catch(() => {});
      }
    } catch (error) {
      if (error instanceof APIError && error.detail) {
        showToast(error.detail, { type: "error" });
      } else {
        showToast(t("Something went wrong"), { type: "error" });
      }
      // Re-throw to allow caller to handle if needed
      throw error;
    }
  }

  async function updateTabAndCaseDetails() {
    await updateTabDetails();
    await updateCaseDetails();
  }

  const userHasPermission =
    hasPermission({
      action: "read",
      target: "case",
      scope: "all",
    }) ||
    hasPermission({
      action: "read",
      target: "case",
      scope: "my_groups",
    });

  if (!userHasPermission) {
    return (
      <ConfigurationError
        message={t(
          "Hmm, it looks like you do not have access to this case. You may need to ask the group admin to be added as a member for access."
        )}
      />
    );
  }

  function getMappedCustomTab(
    customTabItem: components["schemas"]["ClientPortalCaseDetailTab"]
  ) {
    const identityVerificationTab = (
      <IdentityVerificationTab
        caseId={caseId}
        hasNotManuallyVerified={customTabItem.data?.show_notification}
        updateTabAndCaseDetails={updateTabAndCaseDetails}
      />
    );

    const tabMapping: { [key: string]: React.ReactElement } = {
      BASIC_UK_DBS_1: identityVerificationTab,
      STANDARD_UK_DBS_1: identityVerificationTab,
      ENHANCED_UK_DBS_1: identityVerificationTab,
    };

    if (!(customTabItem.check in tabMapping)) {
      return null;
    }

    return tabMapping[`${customTabItem?.check}`];
  }

  return (
    <div className={styles.noScrollWrapper}>
      <SecondaryTopNav
        leftIcon="close"
        onleftIconOnPress={() => navigate(previous.href)}
        testID="case-details-secondary-top-nav"
        titleArea={
          !isMobile
            ? {
                subtitle: `ID: ${caseDetails?.short_id ?? caseId}`,
                title: !caseDetails?.customer_account.auth0_display_name
                  ? caseDetails?.invite_email_address
                  : caseDetails?.customer_account.auth0_display_name,
              }
            : { subtitle: "", title: "" }
        }
      >
        {hasPermission({
          action: "create",
          target: "case",
          scope: "all",
        }) ? (
          caseDetails?.rescreen_frequency ? (
            <GhostButton
              size="small"
              leftIcon="auto_renew"
              onPress={() => setShowEditRescreenModal(true)}
            >
              {t("Edit rescreen")}
            </GhostButton>
          ) : (
            <GhostButton
              size="small"
              leftIcon="auto_renew"
              onPress={() => setShowScheduleRescreenModal(true)}
            >
              {t("Rescreen")}
            </GhostButton>
          )
        ) : null}
        <CopyLinkButton url={location.href} />
      </SecondaryTopNav>
      <Divider />
      <div className={styles.pageContent}>
        <div className={styles.scrollSection}>
          {!caseDetailsIsLoading && caseDetails && (
            <CaseDetailsTitleCard
              decision={caseDetails.decision}
              groupName={
                caseDetails.customer_group?.name ||
                caseDetails.customer_account.auth0_display_name
              }
              applicant={caseDetails?.applicant}
              shortID={caseDetails.short_id}
              orderedByName={caseDetails.ordered_by?.auth0_full_name}
              status={caseDetails.overall_status}
              score={caseDetails.overall_score}
              updateDecision={updateDecision}
              tags={caseDetails.tag_set}
              checks={caseDetails.checks}
              rescreenFrequency={caseDetails?.rescreen_frequency}
              caseId={caseId}
              promptMonitoringEnroll={promptMonitoringEnroll}
              monitoringEligibilityRefreshing={monitoringEligibilityRefreshing}
              onMonitoringPromptHandled={() => setPromptMonitoringEnroll(false)}
            />
          )}
          <Tabs
            gap="space-0"
            selectedKey={currentTab}
            testID="case-details-tabs"
          >
            <TabList
              aria-label={t("Type of case details")}
              className={styles.tabList}
            >
              <Tab href={url} testID="case-details-report-tab">
                {t("CASE Report", "Report")}
              </Tab>
              {clientPortalShowCredentialsOnCaseDetailsPage &&
                credentialRequest && (
                  <Tab
                    id="/credentials"
                    href={`${url}/credentials`}
                    testID="case-details-credentials-tab"
                  >
                    {t("Credentials")}{" "}
                    <NewBadge testID="case-list-review-tab-badge">
                      {credentialRequest?.requested_credential_types.length}
                    </NewBadge>
                  </Tab>
                )}
              <Tab
                id="/activity-log"
                href={`${url}/activity-log`}
                testID="case-details-activity-tab"
              >
                {t("Activity")}
              </Tab>
              {enableDocumentsTabInClientPortal && (
                <Tab
                  id="/documents"
                  href={`${url}/documents`}
                  testID="case-details-documents-tab"
                >
                  {t("Documents")}
                </Tab>
              )}

              {tabDetails?.checks.map(
                (item: components["schemas"]["ClientPortalCaseDetailTab"]) => {
                  if (item.data?.show_tab) {
                    return (
                      <Tab
                        key={item.check}
                        id={`/${item.data?.tab_id}`}
                        href={`${url}/${item.data?.tab_id}`}
                        testID={`${item.data?.tab_id}-tab`}
                      >
                        {item.data?.tab_name}{" "}
                        {item.data?.show_notification && (
                          <DangerBadge type="bold">1</DangerBadge>
                        )}
                      </Tab>
                    );
                  }
                }
              )}

              {clientPortalDisplayGuarantorsCoapplicants &&
                linkedCases.length > 0 && (
                  <Tab
                    id="/linked-cases"
                    href={`${url}/linked-cases`}
                    testID="case-details-linked-cases-tab"
                  >
                    {`${t("Linked cases")} (${linkedCases.length})`}
                  </Tab>
                )}
              {isMobile && (
                <Tab
                  id="/comments"
                  href={`${url}/comments`}
                  testID="case-details-comments-tab"
                >
                  {t("Comments")}
                </Tab>
              )}
              {showAdjudicationTab && (
                <Tab
                  id="/adjudication"
                  href={`${url}/adjudication`}
                  testID="case-details-adjudication-tab"
                >
                  {t("Adjudication")}
                </Tab>
              )}
            </TabList>
            <TabPanel id={currentTab} className={styles.tabPanel}>
              <Routes>
                <Route path="" element={<ReportTab caseId={caseId} />} />
                <Route
                  path="/activity-log"
                  element={<ActivityLogTab caseId={caseId} />}
                />
                {enableDocumentsTabInClientPortal && (
                  <Route
                    path="/documents"
                    element={<DocumentsTab caseId={caseId} />}
                  />
                )}
                {showAdjudicationTab && (
                  <Route
                    path="/adjudication"
                    element={<AdjudicationTab caseId={caseId} />}
                  />
                )}
                {tabDetails?.checks?.map(
                  (
                    item: components["schemas"]["ClientPortalCaseDetailTab"]
                  ) => (
                    <Route
                      key={item.check}
                      path={`/${item.data?.tab_id}`}
                      element={getMappedCustomTab(item)}
                    />
                  )
                )}
                <Route
                  path="/comments"
                  element={
                    <CommentSectionTab
                      comments={caseDetails?.comments ?? []}
                      mutate={updateCaseDetails}
                    />
                  }
                />
                <Route
                  path="/linked-cases"
                  element={<LinkedCasesTab caseId={caseId} />}
                />
                <Route
                  path="/credentials"
                  element={<CredentialsTab caseId={caseId} />}
                />
              </Routes>
            </TabPanel>
          </Tabs>
        </div>
        <MediaQuery sm md type="hide">
          <CommentSection
            comments={caseDetails?.comments ?? []}
            mutate={updateCaseDetails}
          />
        </MediaQuery>
      </div>
      {caseDetails && (
        <EditRescreenModal
          isOpen={showEditRescreenModal}
          caseId={caseDetails?.short_id || ""}
          selectedKey={caseDetails.rescreen_frequency}
          refreshData={updateCaseDetails}
          onClose={() => setShowEditRescreenModal(false)}
        />
      )}
      <ScheduleRescreenModal
        isOpen={showScheduleRescreenModal}
        caseId={caseDetails?.short_id || ""}
        onClose={() => setShowScheduleRescreenModal(false)}
        refreshData={updateCaseDetails}
      />
    </div>
  );
}

const styles = {
  noScrollWrapper: css({
    height: "100vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  }),
  pageContent: css({
    display: "flex",
    flexDirection: "row",
    flex: 1,
    maxHeight: "100%",
    overflow: "hidden",
  }),
  scrollSection: css({
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarGutter: "stable",
    backgroundColor: "var(--surface-sunken)",
  }),
  tabList: css(
    {
      backgroundColor: "var(--surface-base)",
    },
    mq({
      paddingLeft: ["var(--space-4)", "var(--space-8)"],
      paddingRight: ["var(--space-4)", "var(--space-8)"],
    })
  ),
  tabPanel: css(
    mq({
      paddingTop: ["var(--space-3)", "var(--space-5)"],
      paddingBottom: ["var(--space-3)", "var(--space-5)"],
      paddingLeft: [0, "var(--space-8)"],
      paddingRight: [0, "var(--space-8)"],
    })
  ),
};
